/**
 * Extract plain text from a PDF using pdfjs-dist.
 *
 * Items are joined in pdf.js's native (content-stream) order: each text item's
 * string, followed by a newline when pdf.js reports a line break (`hasEOL`) or
 * a space otherwise. Invoice detectors match their regexes against exactly this
 * shape, so this join is fidelity-critical. It lives in `joinPdfText`, which the
 * detection tests reuse (feeding it the real `test-data/*.pdf` documents), so a
 * change to the join is caught there instead of silently drifting the inputs
 * the detectors see.
 *
 * pdfjs is imported dynamically to keep it out of the main bundle, mirroring
 * `renderPdfFirstPageToWebp`. The worker is a Vite-bundled, same-origin asset
 * (not a runtime CDN fetch), so it works offline and avoids cross-origin
 * module-worker fragility on mobile browsers.
 */
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

type PageTextContent = Awaited<ReturnType<PDFPageProxy["getTextContent"]>>;

/**
 * Read one page's text items.
 *
 * Deliberately not `page.getTextContent()`: that helper async-iterates the
 * underlying stream (`for await (const value of readableStream)`), and WebKit
 * does not implement `ReadableStream[Symbol.asyncIterator]`. On iOS — Safari
 * and Chrome alike, both JavaScriptCore — it throws "undefined is not a
 * function". Driving the same stream through an explicit reader is supported
 * everywhere and yields the identical item sequence, since `getTextContent` is
 * only this loop plus a concatenation of the chunks.
 */
const readPageTextItems = async (
  page: PDFPageProxy,
): Promise<PageTextContent["items"]> => {
  const reader = (
    page.streamTextContent() as ReadableStream<PageTextContent>
  ).getReader();
  const items: PageTextContent["items"] = [];
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    items.push(...value.items);
  }
  return items;
};

/**
 * Join a loaded PDF's text items into the flat string the invoice detectors run
 * against. Split out from document loading so tests can pass a document parsed
 * from a real PDF fixture and exercise this exact join end-to-end.
 */
export const joinPdfText = async (pdf: PDFDocumentProxy): Promise<string> => {
  let text = "";
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    for (const item of await readPageTextItems(page)) {
      if ("str" in item) {
        text += item.str + (item.hasEOL ? "\n" : " ");
      }
    }
    text += "\n";
  }
  return text;
};

export const extractPdfText = async (file: File | Blob): Promise<string> => {
  const pdfjsLib = await import("pdfjs-dist");

  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  return joinPdfText(pdf);
};
